import React, { FC, useEffect, useMemo } from 'react';

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  Skeleton,
} from '@chakra-ui/react';
import { RegionDropdown } from 'react-country-region-selector';
import { useFormContext } from 'react-hook-form';

import { useErrorMessage } from '../index';
import { useStyles } from '../index';
import {
  FieldProps,
  CustomFieldSchema,
  ReactSelectFieldStyles,
} from '../types';

import './styles.css';
import { FormLabel } from './FormLabel';

export const StatePicker: FC<FieldProps<CustomFieldSchema>> = ({
  id,
  name,
  field,
  isLoading,
}) => {
  const {
    label,
    isRequired,
    shouldDisplay,
    helpIcon,
    onChange,
    helperText,
    defaultValue,
    value,
    styles = {},
    props,
    helpTooltip,
    onClickHelpIcon,
  } = field;

  const { register, watch, setValue, getValues } = useFormContext();

  const values = watch();

  const fieldStyles = useStyles<ReactSelectFieldStyles>(
    'reactSelectField',
    styles,
  );

  useEffect(() => {
    register(name);
  }, [register, name]);

  useEffect(() => {
    value && setValue(name, value);
  }, [name, value, setValue]);

  const errorMessage = useErrorMessage(name, label);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return isVisible ? (
    <FormControl
      key={`${name}-control`}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
      {...fieldStyles.control}
      marginBottom={'1rem'}
    >
      {!!label && (
        <FormLabel
          htmlFor={name}
          label={label}
          helpIcon={helpIcon}
          styles={fieldStyles.label}
          helpTooltip={helpTooltip}
          isRequired={isRequired}
          onClickHelpIcon={onClickHelpIcon}
        />
      )}
      <Skeleton isLoaded={!isLoading}>
        <RegionDropdown
          {...register(name)}
          id={id}
          // style prop can be passed to this component, but typescript is throwing error
          // @ts-ignore
          style={{
            ...fieldStyles,
          }}
          defaultValue={defaultValue}
          country={props?.country ?? ''}
          value={getValues(name) || defaultValue}
          defaultOptionLabel="Select State"
          onChange={selected => {
            onChange?.(selected);
            setValue(name, selected);
          }}
        />
      </Skeleton>
      {!!helperText && (
        <FormHelperText {...fieldStyles.helperText}>
          {helperText}
        </FormHelperText>
      )}
      <FormErrorMessage {...fieldStyles.errorMessage}>
        {errorMessage}
      </FormErrorMessage>
    </FormControl>
  ) : null;
};
